import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as approvalInitialState } from '~redux/slices/approval';

import { TGetInventoryTransactionListingFilters } from '~types/feat/gold';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { InventoryActionEnum, MetalTypeEnum } from '~utils/enums';

import * as Styles from './_goldInventoryApprovalSearchAndFilterBar.module.scss';

const InventoryTransactionSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();

    const filters = useAppSelector(state => state.approval.pendingInventoryTransactionListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const productList = useAppSelector((state) => state.gold.allProducts);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = approvalInitialState.pendingInventoryTransactionListing.filters;

    const setFilters = (inputFilters: TGetInventoryTransactionListingFilters) => dispatch(combinedActions.approvalSetPendingInventoryTransactionListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingInventoryTransactionListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
        });
        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchCode,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                    branchCode: branchesList.find((branch) => branch.branchName === event.target.value)?.branchCode || '',
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Product Name:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedProductsListOption().sort((a, b) => a.value - b.value),
                value: filters.productName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    productName: event.target.value,
                    productId: productList.find((product) => product.productName === event.target.value)?.productId || 0,
                }),
                optionSelected: !!productList.find((product) => product.productName === filters.productName),
                noOptionsMessage: 'No product found',
            },
        },
        {
            label: 'Transaction Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Restock',
                        value: InventoryActionEnum.Restock,
                    },
                    {
                        label: 'New Product',
                        value: InventoryActionEnum.NewProduct,
                    },
                ],
                value: filters.action,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, action: event.target.value }),
                optionSelected: !!InventoryActionEnum[filters.action.split(' ').join('') as keyof typeof InventoryActionEnum],
                noOptionsMessage: 'No action found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                maxDate: dayjs().toISOString(),
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: MetalTypeEnum[filters.metalType as MetalTypeEnum],
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    metalType: MetalTypeEnum[event.target.value as keyof typeof MetalTypeEnum],
                }),
                optionSelected: !!MetalTypeEnum[filters.metalType as MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default InventoryTransactionSearchAndFilterBar;
